import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import SEOMeta from '../components/SEOMeta';

const PageWrapper = styled.div`
  max-width: 512px;
  margin: 0 auto;
  min-height: 100dvh;
  background: #f6f8f8;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const Header = styled.header`
  background: white;
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
  position: sticky;
  top: 0;
  z-index: 50;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeaderTitle = styled.h1`
  font-size: 18px;
  font-weight: 700;
  color: #1a1f2e;
  margin: 0;
`;

const Content = styled.div`
  padding: 24px 16px;
  background: white;
  min-height: calc(100dvh - 60px);
`;

const Section = styled.section`
  margin-bottom: 28px;
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  color: #1a1f2e;
  margin: 0 0 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Paragraph = styled.p`
  font-size: 14px;
  color: #4b5563;
  line-height: 1.7;
  margin: 0 0 12px;
`;

const List = styled.ul`
  font-size: 14px;
  color: #4b5563;
  line-height: 1.7;
  margin: 0 0 12px;
  padding-left: 20px;
`;

const ListItem = styled.li`
  margin-bottom: 6px;
`;

const UpdateDate = styled.div`
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 24px;
`;

const ContactBox = styled.div`
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px;
  margin-top: 24px;
`;

const ContactTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1a1f2e;
  margin-bottom: 8px;
`;

const ContactEmail = styled.a`
  font-size: 14px;
  color: #2DBCAF;
  text-decoration: none;
`;

const PrivacyPolicyPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <PageWrapper>
      <SEOMeta 
        title="Kebijakan Privasi | KinderGame.id"
        description="Kebijakan privasi aplikasi Kindergame untuk orang tua dan anak"
      />
      
      <Header>
        <BackButton onClick={() => navigate(-1)}>←</BackButton>
        <HeaderTitle>Kebijakan Privasi</HeaderTitle>
      </Header>

      <Content>
        <UpdateDate>Terakhir diperbarui: 16 Maret 2026</UpdateDate>

        <Section>
          <SectionTitle>👋 Pendahuluan</SectionTitle>
          <Paragraph>
            Kindergame adalah aplikasi edukatif yang dirancang untuk membantu <strong>orang tua</strong> dalam 
            mendukung tumbuh kembang anak usia 0-6 tahun. Kami berkomitmen melindungi privasi Anda dan anak Anda.
          </Paragraph>
          <Paragraph>
            Aplikasi ini ditujukan untuk digunakan oleh orang tua atau wali, bukan langsung oleh anak-anak. 
            Orang tua bertanggung jawab atas penggunaan aplikasi dan data yang dimasukkan.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>📝 Data yang Kami Kumpulkan</SectionTitle>
          <Paragraph>Kami mengumpulkan data berikut dengan persetujuan orang tua:</Paragraph>
          <List>
            <ListItem><strong>Data Orang Tua:</strong> Nama, email (dari Google Sign-In)</ListItem>
            <ListItem><strong>Data Anak:</strong> Nama panggilan, tanggal lahir, jenis kelamin</ListItem>
            <ListItem><strong>Data Penggunaan:</strong> Progress permainan, skor, aktivitas stimulasi</ListItem>
            <ListItem><strong>Data Pertumbuhan:</strong> Tinggi dan berat badan (jika diinput)</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>🎯 Tujuan Penggunaan Data</SectionTitle>
          <Paragraph>Data digunakan untuk:</Paragraph>
          <List>
            <ListItem>Menyediakan konten yang sesuai dengan usia anak</ListItem>
            <ListItem>Menyimpan progress dan pencapaian</ListItem>
            <ListItem>Memberikan rekomendasi aktivitas stimulasi</ListItem>
            <ListItem>Memantau grafik pertumbuhan anak</ListItem>
            <ListItem>Meningkatkan kualitas layanan kami</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>🔒 Keamanan Data</SectionTitle>
          <Paragraph>
            Kami menerapkan langkah-langkah keamanan untuk melindungi data Anda:
          </Paragraph>
          <List>
            <ListItem>Enkripsi data saat transmisi (HTTPS)</ListItem>
            <ListItem>Penyimpanan data di server yang aman</ListItem>
            <ListItem>Akses data dibatasi hanya untuk keperluan operasional</ListItem>
            <ListItem>Tidak menjual atau membagikan data kepada pihak ketiga untuk iklan</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>👶 Perlindungan Privasi Anak (COPPA)</SectionTitle>
          <Paragraph>
            Kindergame mematuhi Children's Online Privacy Protection Act (COPPA):
          </Paragraph>
          <List>
            <ListItem>Data anak hanya dikumpulkan melalui orang tua</ListItem>
            <ListItem>Orang tua memberikan persetujuan saat mendaftarkan anak</ListItem>
            <ListItem>Orang tua dapat melihat, mengubah, atau menghapus data anak kapan saja</ListItem>
            <ListItem>Tidak ada iklan bertarget untuk anak-anak</ListItem>
            <ListItem>Tidak ada pembelian dalam aplikasi tanpa verifikasi orang tua</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>📱 Layanan Pihak Ketiga</SectionTitle>
          <Paragraph>Kami menggunakan layanan berikut:</Paragraph>
          <List>
            <ListItem><strong>Google Sign-In:</strong> Untuk autentikasi orang tua</ListItem>
            <ListItem><strong>YouTube:</strong> Untuk konten dongeng (dibuka dengan verifikasi orang tua)</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>✏️ Hak Orang Tua</SectionTitle>
          <Paragraph>Sebagai orang tua, Anda berhak untuk:</Paragraph>
          <List>
            <ListItem>Melihat semua data yang tersimpan tentang anak Anda</ListItem>
            <ListItem>Memperbarui atau mengoreksi data</ListItem>
            <ListItem>Menghapus akun dan semua data terkait</ListItem>
            <ListItem>Menarik persetujuan kapan saja</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>🔄 Perubahan Kebijakan</SectionTitle>
          <Paragraph>
            Kami dapat memperbarui kebijakan privasi ini dari waktu ke waktu. 
            Perubahan signifikan akan diberitahukan melalui aplikasi atau email.
          </Paragraph>
        </Section>

        <ContactBox>
          <ContactTitle>📧 Hubungi Kami</ContactTitle>
          <Paragraph style={{ margin: 0 }}>
            Jika ada pertanyaan tentang kebijakan privasi ini, hubungi kami di:
          </Paragraph>
          <ContactEmail href="mailto:kindergame.id@gmail.com">
            kindergame.id@gmail.com
          </ContactEmail>
        </ContactBox>
      </Content>
    </PageWrapper>
  );
};

export default PrivacyPolicyPage;
